// tornei.ts — le chiamate HTTP del torneo a coppie e i token dei tornei salvati sul telefono.
// Le partite del torneo sono sfide normali: il torneo dà a ciascuno codice e token della sua partita, che si salvano
// come quelli delle sfide (così /c/<codice> entra direttamente) insieme al torneo a cui appartengono.
import { richiesta } from "./api.ts";
import { idDispositivo } from "./dispositivo.ts";
import { avatarAttuale } from "./account.ts";
import { leggiPreferenza, salvaPreferenza } from "./preferenze.ts";
import { salvaToken, type ServerIce } from "./sfide.ts";
import { PERCORSO_TORNEO, PREFERENZE } from "../costanti.ts";
import { indirizzoSito } from "../gioco/Condividi.tsx";

export type StatoTorneo = "attesa" | "semifinali" | "finale" | "finito" | "annullato";
export type Turno = "semi1" | "semi2" | "finale";

export interface InfoTorneo {
  stato: StatoTorneo;
  posti: number;
  organizzatore: string | null;
  /** v020 */
  avatarOrganizzatore?: number | null;
}
export interface VistaTorneo {
  stato: StatoTorneo;
  scadeIl: string;
  io: { posto: number; organizzatore: boolean };
  posti: { posto: number; nome: string; avatar?: number | null }[];
  incontri: { turno: Turno; posti: [number, number]; vincitore: number | null; inCorso: boolean }[];
  vincitore: number | null;
  mia: { turno: Turno; codice: string; token: string; tavolinoDal: string | null } | null;
  guarda: { codice: string } | null;
  spettatori: boolean;
}

const enc = encodeURIComponent;

export function creaTorneo(nome: string, conBot = false): Promise<{ codice: string; token: string; link: string; posto: number }> {
  return richiesta("/api/v1/tornei", { metodo: "POST", corpo: { nome, dispositivo: idDispositivo(), avatar: avatarAttuale(), ...(conBot ? { conBot: true } : {}) } });
}
export function leggiTorneo(codice: string): Promise<InfoTorneo> {
  return richiesta(`/api/v1/tornei/${enc(codice)}`);
}
export function entraTorneo(codice: string, nome: string): Promise<{ token: string; posto: number }> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/entra`, { metodo: "POST", corpo: { nome, dispositivo: idDispositivo(), avatar: avatarAttuale() } });
}
export function statoTorneo(codice: string, token: string): Promise<VistaTorneo> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/stato`, { metodo: "POST", corpo: { token } });
}
export function iniziaTorneo(codice: string, token: string): Promise<void> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/inizia`, { metodo: "POST", corpo: { token } });
}
/** v041: il tester che organizza mette un bot nel primo posto vuoto. */
export function aggiungiBotTorneo(codice: string, token: string): Promise<{ posto: number }> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/bot`, { metodo: "POST", corpo: { token } });
}
export function esciTorneo(codice: string, token: string): Promise<void> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/esci`, { metodo: "POST", corpo: { token } });
}
export function vinciATavolino(codice: string, token: string): Promise<void> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/tavolino`, { metodo: "POST", corpo: { token } });
}
export function annullaTorneo(codice: string, token: string): Promise<void> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/annulla`, { metodo: "POST", corpo: { token } });
}
export function serverIceSpettatore(codice: string, token: string, partita: string): Promise<{ iceServers: ServerIce[]; turn: boolean }> {
  return richiesta(`/api/v1/tornei/${enc(codice)}/ice`, { metodo: "POST", corpo: { token, partita } });
}
export const linkTorneo = (codice: string): string => `${indirizzoSito()}${PERCORSO_TORNEO}${codice}`;

// --- sul telefono -----------------------------------------------------------------------
interface Salvati {
  tornei: Record<string, { token: string; il: number }>;
  /** partita (codice della sfida) → torneo */
  partite: Record<string, { torneo: string; il: number }>;
}
// Un torneo dura al massimo un giorno (attesa + durata massima): dopo due giorni i token non servono più.
const DURATA_MS = 2 * 24 * 3600 * 1000;

function salvati(): Salvati {
  try {
    const v = JSON.parse(leggiPreferenza(PREFERENZE.tornei) ?? "{}") as Partial<Salvati>;
    return { tornei: v.tornei ?? {}, partite: v.partite ?? {} };
  } catch {
    return { tornei: {}, partite: {} };
  }
}
function salva(s: Salvati): void {
  const ora = Date.now();
  const recenti = <T extends { il: number }>(o: Record<string, T>) => Object.fromEntries(Object.entries(o).filter(([, x]) => ora - x.il < DURATA_MS));
  salvaPreferenza(PREFERENZE.tornei, JSON.stringify({ tornei: recenti(s.tornei), partite: recenti(s.partite) }));
}

export function tokenTorneo(codice: string): string | null {
  return salvati().tornei[codice]?.token ?? null;
}
export function salvaTokenTorneo(codice: string, token: string): void {
  const s = salvati();
  s.tornei[codice] = { token, il: Date.now() };
  salva(s);
}
export function dimenticaTorneo(codice: string): void {
  const s = salvati();
  delete s.tornei[codice];
  salva(s);
}
/** La mia partita del torneo: token salvato come quello di una sfida, più il torneo a cui tornare. */
export function preparaPartita(torneo: string, partita: string, token: string): void {
  salvaToken(partita, token);
  const s = salvati();
  s.partite[partita] = { torneo, il: Date.now() };
  salva(s);
}
/** Il torneo di una partita (null = sfida normale). */
export function torneoDellaPartita(partita: string): string | null {
  return salvati().partite[partita]?.torneo ?? null;
}
